import { z } from 'zod';
import type { CompetitorCreatorAnalysis, NoteAnalysis } from './analysis';

export const IngestPlatform = z.enum(['xhs', 'x', 'douyin', 'bili', 'weibo']);
export type IngestPlatform = z.infer<typeof IngestPlatform>;
export const NoteCrawlPurpose = z.enum(['self_diag', 'competitor', 'topic', 'public']);
export type NoteCrawlPurpose = z.infer<typeof NoteCrawlPurpose>;

export const NotesListQuery = z.object({
  platform: IngestPlatform.optional(),
  account_id: z.string().uuid().optional(),
  crawl_purpose: NoteCrawlPurpose.optional(),
  created_after: z.string().datetime().optional(),
  created_before: z.string().datetime().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type NotesListQuery = z.infer<typeof NotesListQuery>;

export const IngestMediaInput = z.object({
  position: z.number().int().nonnegative(),
  media_type: z.enum(['image', 'video']),
  original_url: z.string().max(2048).nullable().optional(),
  mime_type: z.string().nullable().optional(),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
});
export type IngestMediaInput = z.infer<typeof IngestMediaInput>;

// 创作者中心独有的真实指标字段：仅 crawl_purpose='self_diag' 可携带（public/competitor/topic 恒空）。
// 此清单同时驱动下方 note 级 .superRefine 的「非 self_diag 携带任一即拒绝」不变式。
// ⚠️ 此处是 ingest 契约的唯一真相源：新增/删除字段时同步本清单与下方 engagement 对象即可。
// workers/ingest 只复用导出的 IngestNotesRequest（不得重声明字段），故没有第二份副本需要同步（ME-34）。
const SELF_DIAG_ENGAGEMENT_KEYS = [
  'impressions',
  'reads',
  'views',
  'read_duration_ms_avg',
  'follower_delta',
  'follower_sources',
] as const;

export const IngestNoteInput = z
  .object({
    external_id: z.string().min(1),
    content_type: z.enum(['text', 'image', 'video', 'mixed']),
    crawl_purpose: NoteCrawlPurpose,
    title: z.string().nullable().optional(),
    summary: z.string().max(512).nullable().optional(),
    body: z.string().nullable().optional(),
    author_external_id: z.string().nullable().optional(),
    author_external_name: z.string().nullable().optional(),
    source_url: z.string().max(2048).nullable().optional(),
    published_external_at: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    engagement: z
      .object({
        // 公开互动数：竞品爬公开页也能拿到，任意 crawl_purpose 均可携带。
        likes: z.number().int().nonnegative().optional(),
        collects: z.number().int().nonnegative().optional(),
        comments: z.number().int().nonnegative().optional(),
        shares: z.number().int().nonnegative().optional(),
        plays: z.number().int().nonnegative().optional(),
        // —— 创作者中心真实指标（竞品拿不到，仅 self_diag 填，见上方清单 + superRefine）——
        impressions: z.number().int().nonnegative().optional(), // 曝光量
        reads: z.number().int().nonnegative().optional(), // 图文阅读量
        views: z.number().int().nonnegative().optional(), // 视频观看量
        read_duration_ms_avg: z.number().nonnegative().optional(), // 平均阅读时长（毫秒，均值允许小数）
        // 区间净涨粉：有符号、可为负（净掉粉是有效诊断信号），不套非负校验。
        follower_delta: z.number().int().optional(),
        // 新增关注来源拆分（毛数）；与 follower_delta 不做交叉校验（净值含取关，对不齐正常）。
        follower_sources: z
          .object({
            search: z.number().int().nonnegative().optional(),
            feed: z.number().int().nonnegative().optional(),
            follow_page: z.number().int().nonnegative().optional(),
            profile: z.number().int().nonnegative().optional(),
            other: z.number().int().nonnegative().optional(),
          })
          .partial()
          .optional(),
      })
      .partial()
      .nullable()
      .optional(),
    media: z.array(IngestMediaInput).max(50).optional(),
  })
  .superRefine((note, ctx) => {
    const positions = new Set<number>();
    note.media?.forEach((media, index) => {
      if (positions.has(media.position)) {
        ctx.addIssue({
          code: 'custom',
          path: ['media', index, 'position'],
          message: `media.position ${media.position} 重复`,
        });
      }
      positions.add(media.position);
    });
    const engagement = note.engagement;
    if (!engagement) return;

    // S1 口径：小红书单篇为图文 XOR 视频，reads（图文阅读）与 views（视频观看）正值互斥。
    // 允许 views=0 / reads=0 这类显式零值，拒绝同篇同时填两个正值，避免漏斗消费量重复计数。
    if ((engagement.reads ?? 0) > 0 && (engagement.views ?? 0) > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['engagement', 'views'],
        message: 'engagement.reads 与 engagement.views 按笔记类型互斥，不能同时为正数',
      });
    }

    // 不变式：创作者中心真实指标 ⟺ self_diag。非 self_diag 携带任一即在 ingest 边界 fail-fast，
    // 不静默忽略——宁可炸出来也不要吞掉采集端回归。
    if (note.crawl_purpose === 'self_diag') return;
    for (const key of SELF_DIAG_ENGAGEMENT_KEYS) {
      if (engagement[key] !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['engagement', key],
          message: `engagement.${key} 仅允许在 crawl_purpose='self_diag' 时携带（当前 crawl_purpose='${note.crawl_purpose}'）`,
        });
      }
    }
  });
export type IngestNoteInput = z.infer<typeof IngestNoteInput>;

export const IngestNotesRequest = z
  .object({
    platform: IngestPlatform,
    upload_lifecycle: z.literal('finalize_v1').optional(),
    account_id: z.string().min(1).nullable().optional(),
    account_binding_version: z.number().int().positive().nullable().optional(),
    sync_run_id: z.string().uuid().nullable().optional(),
    detail_task_id: z.string().uuid().nullable().optional(),
    detail_lease_id: z.string().uuid().nullable().optional(),
    notes: z.array(IngestNoteInput).min(1).max(100),
  })
  .superRefine((request, ctx) => {
    const selfNotes = request.notes.filter((note) => note.crawl_purpose === 'self_diag');
    if (selfNotes.length === 0) {
      if (request.account_id != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['account_id'],
          message: '非 self_diag 请求不得携带 account_id',
        });
      }
      if (request.account_binding_version != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['account_binding_version'],
          message: '非 self_diag 请求不得携带 account_binding_version',
        });
      }
      if (request.sync_run_id != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['sync_run_id'],
          message: '非 self_diag 请求不得携带 sync_run_id',
        });
      }
      if (request.detail_task_id != null || request.detail_lease_id != null) {
        ctx.addIssue({
          code: 'custom',
          path: ['detail_task_id'],
          message: '非 self_diag 请求不得携带详情任务租约',
        });
      }
      return;
    }

    if (selfNotes.length !== request.notes.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['notes'],
        message: 'self_diag 不得与其他 crawl_purpose 混合批量上报',
      });
    }
    if (!request.account_id) {
      ctx.addIssue({
        code: 'custom',
        path: ['account_id'],
        message: 'self_diag 必须携带 account_id',
      });
    }
    if (!request.account_binding_version) {
      ctx.addIssue({
        code: 'custom',
        path: ['account_binding_version'],
        message: 'self_diag 必须携带 account_binding_version',
      });
    }
    if (!request.sync_run_id) {
      ctx.addIssue({
        code: 'custom',
        path: ['sync_run_id'],
        message: 'self_diag 必须携带 sync_run_id',
      });
    }
    if (Boolean(request.detail_task_id) !== Boolean(request.detail_lease_id)) {
      ctx.addIssue({
        code: 'custom',
        path: ['detail_lease_id'],
        message: 'detail_task_id 与 detail_lease_id 必须同时提供',
      });
    }
    request.notes.forEach((note, index) => {
      if (!note.author_external_id) {
        ctx.addIssue({
          code: 'custom',
          path: ['notes', index, 'author_external_id'],
          message: 'self_diag 必须携带 author_external_id',
        });
      }
    });
  });
export type IngestNotesRequest = z.infer<typeof IngestNotesRequest>;

export const MediaTicket = z.object({
  position: z.number().int().nonnegative(),
  media_id: z.string().min(1),
  upload_token: z.string().min(1),
  expires_at: z.string().datetime(),
});
export type MediaTicket = z.infer<typeof MediaTicket>;

export const NoteResult = z.object({
  external_id: z.string().min(1),
  note_id: z.string().min(1),
  status: z.enum(['created', 'duplicate']),
  media: z.array(MediaTicket),
});
export type NoteResult = z.infer<typeof NoteResult>;

export const IngestNotesResponse = z.object({
  results: z.array(NoteResult),
});
export type IngestNotesResponse = z.infer<typeof IngestNotesResponse>;

export const MediaUploadResponse = z.object({
  media_id: z.string().min(1),
  r2_key: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  status: z.literal('stored'),
});
export type MediaUploadResponse = z.infer<typeof MediaUploadResponse>;

export const NoteUploadStateResponse = z.object({
  note_id: z.string().min(1),
  status: z.enum(['uploading', 'completed', 'failed']),
  media: z.array(
    z.object({
      position: z.number().int().nonnegative(),
      media_id: z.string().min(1),
      status: z.enum(['stored', 'missing']),
    }),
  ),
  media_tickets: z.array(MediaTicket),
});
export type NoteUploadStateResponse = z.infer<typeof NoteUploadStateResponse>;

export const NoteUploadFinalizeResponse = z.object({
  note_id: z.string().min(1),
  status: z.literal('completed'),
});
export type NoteUploadFinalizeResponse = z.infer<typeof NoteUploadFinalizeResponse>;

export type NoteMediaSummary = {
  id: string;
  position: number;
  media_type: 'image' | 'video';
  mime_type: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  signed_url: string;
  expires_at: string;
};

export type NoteSummary = {
  id: string;
  platform: IngestPlatform;
  external_id: string;
  account_id: string | null;
  author_external_id: string | null;
  author_external_name: string | null;
  content_type: 'text' | 'image' | 'video' | 'mixed';
  title: string | null;
  summary: string | null;
  media_count: number;
  tags: string[];
  engagement: Record<string, number | null>;
  crawl_purpose: NoteCrawlPurpose;
  source_url: string | null;
  published_external_at: string | null;
  crawled_at: string;
  created_at: string;
  updated_at: string;
  media: NoteMediaSummary[];
  /** ME-24：analyzer 产出的最小洞察集（摘要/标签/分数）。从未分析时 status='pending'。 */
  analysis: NoteAnalysis;
  detail_sync_status: import('./account').NoteDetailSyncStatus;
  detail_sync_failure_code: import('./account').AccountSyncFailureCode | null;
};

export type NotesListResponse = {
  items: NoteSummary[];
  next_cursor: string | null;
};

export type NoteDetailResponse = NoteSummary & {
  body: string | null;
};

export type CompetitorCreatorSummary = {
  id: string;
  platform: IngestPlatform;
  external_id: string;
  nickname: string | null;
  avatar_signed_url: string | null;
  follower_count: number | null;
  bio: string | null;
  source_url: string | null;
  watch_enabled: boolean;
  last_refreshed_at: string | null;
  next_refresh_at: string | null;
  analysis: CompetitorCreatorAnalysis | null;
  crawled_at: string;
  created_at: string;
  updated_at: string;
};

export type CreatorsListResponse = {
  items: CompetitorCreatorSummary[];
  next_cursor: string | null;
};
